
  import { computed, defineComponent, ref, watch, onMounted } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { hideModal } from '@/core/helpers/dom';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useStore } from 'vuex';
  import { Packages } from '@/store/modules/Shipments/ShipmentsModule';
  import { useI18n } from 'vue-i18n/index';
  import Multiselect from '@vueform/multiselect';

  export default defineComponent({
    name: 'packages-editing',
    props: {
      editData: { type: Number, required: true },
      statusId: { type: String, required: true },
    },
    emit: ['list-changed'],
    components: {
      ErrorMessage,
      Field,
      Form,
      Multiselect,
    },
    async setup(props, { emit }) {
      const submitButtonRef = ref<null | HTMLButtonElement>(null);
      const newPackagesModalRef = ref<null | HTMLElement>(null);
      const store = useStore();
      const { t, te } = useI18n();
      const selectedGovernorate = ref<number>(0);
      const packages = ref<Packages | null>({
        id: '',
        holderName: '',
        areaId: '',
        docId: '',
        address: '',
        recipientPhoneNumber: '',
        secondRecipientPhoneNumber: undefined,
        qrCodeImage: '',
        trackNumber: '',
        notes: '',
      });
      const selectedArea = ref<string | undefined>('');

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      watch(
        () => selectedGovernorate.value,
        async () => {
          if (selectedGovernorate.value) {
            selectedArea.value = '';
            await store.dispatch(
              Actions.GET_SHIPMENT_AREAS,
              selectedGovernorate.value
            );
          }
        }
      );

      watch(
        () => props.editData,
        async (groupId) => {
          const { data } = await store.dispatch(
            Actions.GET_SHIPMENT_PACKAGE,
            groupId
          );
          packages.value = data.data[0];
          if (packages.value !== null) {
            selectedArea.value = packages.value.areaId;
          }
        }
      );

      const recipientPhoneNumber = computed(() => {
        if (packages.value) {
          const mobileString = String(packages.value.recipientPhoneNumber);
          const last7Digits = mobileString.slice(-10);
          return String(last7Digits);
        }
        return '';
      });

      const secondRecipientPhoneNumber = computed(() => {
        if (packages.value?.secondRecipientPhoneNumber) {
          const mobileString = String(
            packages.value.secondRecipientPhoneNumber
          );
          const last7Digits = mobileString.slice(-10);
          return String(last7Digits);
        }
        return undefined;
      });

      const validationSchema = Yup.object().shape({
        holderName: Yup.string()
          .required(() => translate('HOLDER_NAME_IS_REQUIRED_FIELD'))
          .label('holder Name'),
        docId: Yup.string()
          .required(() => translate('DOC_ID_IS_REQUIRED_FIELD'))
          .label('docId'),
        recipientPhoneNumber: Yup.string()
          .matches(/^(77|78|75)\d{8}$/, 'Mobile number is not valid')
          .required(() => translate('RECIPIENT_PHONE_NUMBER_IS_REQUIRED_FIELD'))
          .label('Mobile'),
        secondRecipientPhoneNumber: Yup.string().test(
          'is-valid-mobile',
          'Mobile number is not valid',
          function (value) {
            if (value === null) return true;
            return !value || /^(77|78|75)\d{8}$/.test(value);
          }
        ),
        address: Yup.string()
          .required(() => translate('ADDRESS_IS_REQUIRED_FIELD'))
          .label('address'),
      });

      const submit = async (values) => {
        if (!submitButtonRef.value) {
          return;
        }

        //Disable button
        submitButtonRef.value.disabled = true;
        // Activate indicator
        submitButtonRef.value.setAttribute('data-kt-indicator', 'on');
        values.statusId = props.statusId;
        values.areaId = selectedArea.value;
        delete values.governorateid;
        values.recipientPhoneNumber = String(
          '964' + values.recipientPhoneNumber
        );
        if (values.secondRecipientPhoneNumber !== undefined) {
          values.secondRecipientPhoneNumber = String(
            '964' + values.secondRecipientPhoneNumber
          );
        }

        await store.dispatch(Actions.UPDATE_SHIPMENTS_PACKAGE, {
          data: values,
          shipmentId: packages.value?.id,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButtonRef.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButtonRef.value!.disabled = false;
        } else {
          const { data } = await store.dispatch(
            Actions.GET_SHIPMENT_PACKAGE,
            packages.value?.id
          );
          packages.value = data.data[0];
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_PROPERTY'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            emit('list-changed');
            hideModal(newPackagesModalRef.value);
            //Deactivate indicator
            submitButtonRef.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
            submitButtonRef.value!.disabled = false;
          });
        }
      };

      const areas = computed(() => store.getters.getAllAreasList);
      const areasTitle = () => {
        return areas.value.map((i) => {
          return { title: i.titleTranslate, id: i.id };
        });
      };

      const governorate = computed(() => store.getters.allGovernoratesList);
      const governorateTitle = () => {
        return governorate.value.map((i) => {
          return {
            title: i.title.find((r) => r.languageCode === 'en')?.title,
            id: i.id,
          };
        });
      };

      onMounted(async () => {
        await store.dispatch(Actions.GET_ALL_GOVERNORATES);
        await store.dispatch(Actions.GET_SHIPMENT_AREAS);
      });

      return {
        governorateTitle,
        recipientPhoneNumber,
        secondRecipientPhoneNumber,
        governorate,
        areasTitle,
        selectedGovernorate,
        selectedArea,
        packages,
        translate,
        validationSchema,
        submit,
        submitButtonRef,
        newPackagesModalRef,
        areas: computed(() => store.getters.hubAreasList),
      };
    },
  });
